import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { posAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import LoadingSpinner from '../../components/LoadingSpinner';
import { format } from 'date-fns';

// --- Animations ---
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;



// --- Styled Components ---

const Page = styled.div`
  position: relative;
  min-height: 100vh;
  padding: 3rem 2rem;
  
  @media (min-width: 1024px) {
    padding-top: 110px;
  }
  background-color: #050505;
  color: #e5edff;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;

  /* Dynamic Mesh Background */
  background-image: 
    radial-gradient(at 0% 0%, rgba(41, 151, 255, 0.15) 0px, transparent 50%),
    radial-gradient(at 100% 0%, rgba(255, 41, 105, 0.1) 0px, transparent 50%),
    radial-gradient(at 100% 100%, rgba(0, 224, 255, 0.12) 0px, transparent 50%),
    radial-gradient(at 0% 100%, rgba(41, 151, 255, 0.08) 0px, transparent 50%);
  background-attachment: fixed;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
  animation: ${fadeIn} 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 3rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100px;
    height: 1px;
    background: linear-gradient(90deg, #2997ff, transparent);
  }
`;

const HeaderTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;
  background: linear-gradient(180deg, #fff 0%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(165, 180, 252, 0.2);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
`;

const StatsBadge = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.75rem 1.25rem;
  border-radius: 99px;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  }
`;

const PulseDot = styled.div`
  width: 8px;
  height: 8px;
  background: #00e0ff;
  border-radius: 50%;
  box-shadow: 0 0 10px #00e0ff;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 1px solid rgba(0, 224, 255, 0.3);
    animation: ${props => props.animate ? css`${float} 2s infinite ease-in-out` : 'none'};
  }
`;

const StatText = styled.span`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;

  strong {
    color: #fff;
    margin-left: 0.4rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 2rem;
`;

const Card = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 280px;
  background: rgba(20, 20, 25, 0.4);
  border-radius: 24px;
  padding: 2rem;
  text-decoration: none;
  color: inherit;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
  group: card;

  /* Glass Reflection */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 200%;
    height: 100%;
    background: linear-gradient(
      115deg,
      transparent 0%,
      rgba(255, 255, 255, 0.03) 45%,
      rgba(255, 255, 255, 0.08) 50%,
      rgba(255, 255, 255, 0.03) 55%,
      transparent 100%
    );
    transform: skewX(-20deg);
    transition: left 0.6s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-8px) scale(1.01);
    background: rgba(30, 30, 40, 0.5);
    border-color: rgba(41, 151, 255, 0.3);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(41, 151, 255, 0.1);

    &::before {
      left: 100%;
      transition: left 0.8s ease;
    }
  }
`;

const CardGlow = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(41, 151, 255, 0.15), transparent 70%);
  top: -100px;
  right: -100px;
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;

  ${Card}:hover & {
    opacity: 1;
  }
`;

const CardContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const EventTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
  color: #fff;
`;

const EventDate = styled.div`
  font-size: 0.95rem;
  color: #2997ff;
  font-weight: 500;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '';
    width: 4px;
    height: 4px;
    background: currentColor;
    border-radius: 50%;
  }
`;

const VenueInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-bottom: auto;
  
  svg {
    width: 16px;
    height: 16px;
    opacity: 0.7;
  }
`;

const CardFooter = styled.div`
  margin-top: auto;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AllocationBadge = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.05);
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  font-weight: 500;
  letter-spacing: 0.02em;
`;

const ActionArrow = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  transition: all 0.3s ease;

  ${Card}:hover & {
    background: #2997ff;
    transform: translateX(4px);
  }
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem 2rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 24px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  text-align: center;

  h3 {
    font-size: 1.5rem;
    margin: 0 0 1rem 0;
    color: rgba(255, 255, 255, 0.9);
  }

  p {
    color: rgba(255, 255, 255, 0.5);
    max-width: 400px;
    line-height: 1.6;
  }
`;

const PosDashboardPage = () => {
  const { user } = useAuthStore();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await posAPI.getAgentEvents(user.id);
        setEvents(data);
      } catch (err) {
        console.error('Error fetching agent events:', err);
        setError('Failed to load assigned events.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchEvents();
    }
  }, [user]);

  if (loading) return <LoadingSpinner />;

  return (
    <Page>
      <ContentContainer>
        <Header>
          <HeaderTitleGroup>
            <Title>POS Dashboard</Title>
            <Subtitle>Welcome back, {user?.firstName}. Ready to sell?</Subtitle>
          </HeaderTitleGroup>

          <StatsBadge>
            <PulseDot animate />
            <StatText>
              Active Events <strong>{events.length}</strong>
            </StatText>
          </StatsBadge>
        </Header>

        {error && (
          <div style={{
            background: 'rgba(255, 59, 48, 0.1)',
            border: '1px solid rgba(255, 59, 48, 0.2)',
            color: '#ff3b30',
            padding: '1rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            {error}
          </div>
        )}

        {events.length === 0 ? (
          <EmptyState>
            <h3>No Events Assigned</h3>
            <p>You haven't been assigned as a sale agent for any events yet. Contact an administrator to get started.</p>
          </EmptyState>
        ) : (
          <Grid>
            {events.map((access) => (
              <Card key={access.id} to={`/pos/event/${access.eventId}`}>
                <CardGlow />
                <CardContent>
                  <EventTitle>{access.eventName}</EventTitle>
                  <EventDate>
                    {access.eventStartTime
                      ? format(new Date(access.eventStartTime), 'MMM d, yyyy • h:mm a')
                      : 'Date TBA'}
                  </EventDate>

                  <VenueInfo>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {access.eventVenueName || 'Venue TBA'}
                  </VenueInfo>

                  <CardFooter>
                    <AllocationBadge>
                      Limit: {access.allocationLimit > 2000000000 ? '∞' : access.allocationLimit}
                    </AllocationBadge>
                    <ActionArrow>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </ActionArrow>
                  </CardFooter>
                </CardContent>
              </Card>
            ))}
          </Grid>
        )}
      </ContentContainer>
    </Page>
  );
};

export default PosDashboardPage;
